import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

function Membership() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      <SEO
        title="Supreme Monthly Spa Membership in Dallas | Healing Lotus"
        description="Save 20% on every spa service with our $209/month Supreme Membership at Healing Lotus in Dallas. Credits, priority booking, and exclusive member perks."
        path="/membership"
      />
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">Supreme Spa Membership</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">Unlock exclusive benefits and transform your wellness journey</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Serene spa wellness experience"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Membership Hero */}
          <section className="mb-20">
            <div className="bg-gradient-to-br from-[#B2D8C5]/10 to-[#F7CAC9]/10 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#232347] mb-4">
                Supreme Monthly Membership
              </h2>
              <div className="inline-block bg-white rounded-xl shadow-xl p-8 mb-6">
                <div className="text-5xl md:text-6xl font-bold text-[#B2D8C5] mb-2">$209</div>
                <div className="text-lg text-[#232347]/70">per month</div>
                <div className="text-sm text-[#232347]/60 mt-2">Auto-renews monthly · Cancel after 2 months</div>
              </div>
              <Link
                to="/join"
                className="inline-block bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-10 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
              >
                Join Now
              </Link>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-4 text-center">
              Exclusive Member Benefits
            </h2>
            <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto">
              Join and enjoy a luxury experience designed just for you
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Monthly Treatments */}
              <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#B2D8C5]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-serif font-semibold text-[#232347] mb-4 text-center">Monthly Exclusive Treatments</h3>
                <ul className="space-y-3 text-[#232347]/80 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#B2D8C5] mr-2">✓</span>
                    <span>La Mer Series "The Revitalizing Hydration Facial" - Deep hydration (1x/month)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B2D8C5] mr-2">✓</span>
                    <span>Eye Area Massage (2x/month) - Relieve fatigue, brighten eyes</span>
                  </li>
                  <li className="text-xs text-[#232347]/60 italic mt-2">
                    *Eye treatment not available as standalone service
                  </li>
                </ul>
              </div>

              {/* Member Privileges */}
              <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#F7CAC9]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h3 className="text-xl font-serif font-semibold text-[#232347] mb-4 text-center">Member-Only Privileges</h3>
                <ul className="space-y-3 text-[#232347]/80 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#F7CAC9] mr-2">★</span>
                    <span>20% discount on all services (except medical beauty)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F7CAC9] mr-2">★</span>
                    <span>Priority booking - no waiting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F7CAC9] mr-2">★</span>
                    <span>Holiday exclusive gifts & free sound healing salons</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F7CAC9] mr-2">★</span>
                    <span>Early access to new products & treatments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F7CAC9] mr-2">★</span>
                    <span>Birthday surprise: Choose any service from our menu</span>
                  </li>
                </ul>
              </div>

              {/* Convenient Monthly Plan */}
              <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#B2D8C5]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-serif font-semibold text-[#232347] mb-4 text-center">Effortless Monthly Plan</h3>
                <ul className="space-y-3 text-[#232347]/80 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#B2D8C5] mr-2">💳</span>
                    <span>Automatic monthly billing - hassle-free renewal</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B2D8C5] mr-2">💳</span>
                    <span>Cancel after 2 months - minimum commitment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B2D8C5] mr-2">💳</span>
                    <span>Experience VIP care at every visit</span>
                  </li>
                </ul>
                <div className="mt-6 p-4 bg-[#B2D8C5]/10 rounded-lg text-center">
                  <p className="text-sm font-medium text-[#232347]">
                    Make every month your luxury healing time
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Value Comparison */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">
              Exceptional Value
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-4xl mx-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#232347] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left">Service</th>
                      <th className="px-6 py-4 text-center">Regular Price</th>
                      <th className="px-6 py-4 text-center">Member Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4">La Mer Hydration Facial</td>
                      <td className="px-6 py-4 text-center text-gray-500">$240</td>
                      <td className="px-6 py-4 text-center text-[#B2D8C5] font-semibold">Included</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Eye Area Massage (2x)</td>
                      <td className="px-6 py-4 text-center text-gray-500">$100</td>
                      <td className="px-6 py-4 text-center text-[#B2D8C5] font-semibold">Included</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">20% Discount on Services</td>
                      <td className="px-6 py-4 text-center text-gray-500">—</td>
                      <td className="px-6 py-4 text-center text-[#B2D8C5] font-semibold">Included</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Priority Booking</td>
                      <td className="px-6 py-4 text-center text-gray-500">—</td>
                      <td className="px-6 py-4 text-center text-[#B2D8C5] font-semibold">Included</td>
                    </tr>
                    <tr className="bg-[#B2D8C5]/10 font-semibold">
                      <td className="px-6 py-4">Monthly Total Value</td>
                      <td className="px-6 py-4 text-center text-gray-500">$340+</td>
                      <td className="px-6 py-4 text-center text-[#B2D8C5] text-xl">$209</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="bg-[#F7CAC9]/10 px-6 py-4 text-center">
                <p className="text-[#232347] font-medium">
                  Save over $130 per month plus exclusive perks!
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">
              Membership FAQs
            </h2>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md p-8 max-w-4xl mx-auto">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">How do I cancel my membership?</h3>
                  <p className="text-[#232347]/70">After your first 2 months, you can cancel by contacting us at least 3 days before your next billing date. No cancellation fees after the minimum commitment period.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">What if I don't use my monthly treatments?</h3>
                  <p className="text-[#232347]/70">Unused monthly treatments do not roll over to the next month. However, your member discount and other privileges remain active.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">Can I pause my membership?</h3>
                  <p className="text-[#232347]/70">Yes! You can pause your membership for up to 2 months per year if needed. Just let us know in advance.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">What services are excluded from the member discount?</h3>
                  <p className="text-[#232347]/70">The 20% member discount applies to all services except medical beauty treatments (injectables, lasers, etc.).</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">When do I get billed?</h3>
                  <p className="text-[#232347]/70">Your membership auto-renews on the same date each month. You'll receive a reminder email 3 days before billing.</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-[#B2D8C5]/20 to-[#F7CAC9]/20 rounded-xl p-12">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-4">
                Ready to Begin Your Journey?
              </h2>
              <p className="text-[#232347]/80 mb-8 max-w-2xl mx-auto">
                Join Healing Lotus Supreme Membership today and transform every month into your luxury healing time.
              </p>
              <Link
                to="/join"
                className="inline-block bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-10 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
              >
                Join Now - $209/month
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Membership;
